import { MessagesLandingMenuItem } from './types'

export const DEFAULT_MENU_NAME = 'Default'

/**
 * Helper function to create a deep copy of the data
 */
export function deepCopy<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj))
}

export type CustomMenuMetadata = {
  key: string
  name: string
}

export function parseMenuItems(definition?: string | null): MessagesLandingMenuItem[] {
  if (!definition) {
    return []
  }

  try {
    const parsedDefinition = JSON.parse(definition) as { items?: MessagesLandingMenuItem[] }
    return parsedDefinition.items ?? []
  } catch {
    return []
  }
}

export function parseCustomMenuMetadata(definition?: string | null): CustomMenuMetadata {
  if (!definition) {
    return { key: '', name: '' }
  }

  try {
    const parsedDefinition = JSON.parse(definition) as { key?: string; name?: string }
    return {
      key: parsedDefinition.key?.trim() ?? '',
      name: parsedDefinition.name?.trim() ?? '',
    }
  } catch {
    return { key: '', name: '' }
  }
}

/** True when the API populates `key` on list responses (key-based backend). */
export function inferBackendUsesMenuKeys(
  menus: ReadonlyArray<{ key?: string | null }> | undefined
): boolean {
  if (!menus?.length) {
    return true
  }

  return menus.some((menu) => Boolean(menu.key?.trim()))
}

export function resolveCustomMenuIdentity(menu: {
  key?: string | null
  name?: string | null
  definition?: string | null
}): CustomMenuMetadata | null {
  const fromDefinition = parseCustomMenuMetadata(menu.definition)
  const key = menu.key?.trim() || fromDefinition.key
  if (!key) {
    return null
  }

  const name = menu.name?.trim() || fromDefinition.name || key
  return { key, name }
}

export function serializeCustomMenuDefinition(
  menuKey: string,
  menuName: string,
  items: MessagesLandingMenuItem[]
): string {
  return JSON.stringify({ key: menuKey, name: menuName, items })
}

export function toMenuKeySlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
}

export function serializeMenuLayoutForClipboard(items: MessagesLandingMenuItem[]): string {
  return JSON.stringify({ items }, null, 2)
}

export function parseMenuLayoutFromClipboard(text: string): MessagesLandingMenuItem[] | null {
  try {
    const parsed = JSON.parse(text) as { items?: MessagesLandingMenuItem[] } | MessagesLandingMenuItem[]

    if (Array.isArray(parsed)) {
      return parsed
    }

    if (Array.isArray(parsed.items)) {
      return parsed.items
    }

    return null
  } catch {
    return null
  }
}
